import log from 'loglevel';
import type { Readable } from 'stream';

import DxfArrayScanner from './DxfArrayScanner';
import type { IGroup, GroupValue } from './DxfArrayScanner';
import { debugCode } from './ParseHelpers';
import { DXFSymbol } from './constants';
import Face from './entities/3dface';
import Arc from './entities/arc';
import AttDef from './entities/attdef';
import Circle from './entities/circle';
import Dimension from './entities/dimension';
import Ellipse from './entities/ellipse';
import type IGeometry from './entities/geometry';
import type { EntityName, IEntity } from './entities/geometry';
import Insert from './entities/insert';
import Leader from './entities/leader';
import Line from './entities/line';
import LWPolyline from './entities/lwpolyline';
import MLeader from './entities/mleader';
import MLine from './entities/mline';
import MText from './entities/mtext';
import Point from './entities/point';
import Polyline from './entities/polyline';
import Solid from './entities/solid';
import Spline from './entities/spline';
import Text from './entities/text';
import Vertex from './entities/vertex';
import { parseBlocks, serializeBlocks } from './sections/blocks';
import type { Blocks } from './sections/blocks';
import { parseClasses, serializeClasses } from './sections/classes';
import type { IClasses } from './sections/classes';
import { parseEntities, serializeEntities } from './sections/entities';
import { parseHeader, serializeHeader } from './sections/header';
import type { Header } from './sections/header';
import { parseObjects, serializeObjects } from './sections/objects';
import type { IObject } from './sections/objects';
import { parseTables, serializeTables } from './sections/tables';
import type { ITables } from './sections/tables';
import type { DxfEntity } from './types/DxfEntity';

//log.setLevel('trace');
//log.setLevel('debug');
//log.setLevel('info');
//log.setLevel('warn');
log.setLevel('error');
//log.setLevel('silent');

export type IDxf = {
  header?: Header;
  entities?: DxfEntity[];
  blocks?: Blocks;
  tables?: ITables;
  classes?: IClasses;
  objects?: IObject[];
};

function registerDefaultEntityHandlers(dxfParser: DxfParser) {
  // Supported entities here (some entity code is still being refactored into this flow)
  dxfParser.registerEntityHandler(Arc);
  dxfParser.registerEntityHandler(AttDef);
  dxfParser.registerEntityHandler(Circle);
  dxfParser.registerEntityHandler(Dimension);
  dxfParser.registerEntityHandler(Ellipse);
  dxfParser.registerEntityHandler(Face);
  dxfParser.registerEntityHandler(Insert);
  dxfParser.registerEntityHandler(LWPolyline);
  dxfParser.registerEntityHandler(Line);
  dxfParser.registerEntityHandler(Leader);
  dxfParser.registerEntityHandler(MLeader);
  dxfParser.registerEntityHandler(MLine);
  dxfParser.registerEntityHandler(MText);
  dxfParser.registerEntityHandler(Point);
  dxfParser.registerEntityHandler(Polyline);
  dxfParser.registerEntityHandler(Solid);
  dxfParser.registerEntityHandler(Spline);
  dxfParser.registerEntityHandler(Text);
  dxfParser.registerEntityHandler(Vertex);
}

export type ParseState = {
  readonly scanner: DxfArrayScanner;
  readonly dxf: IDxf;
  readonly entityHandlers: Partial<Record<EntityName, IGeometry<IEntity>>>;
  lastHandle: number;
};

const parseAll = (parseState: ParseState): void => {
  const { dxf, scanner } = parseState;
  let curr = scanner.next();
  while (!scanner.isEOF()) {
    if (curr.code === 0 && curr.value === DXFSymbol.Section) {
      curr = scanner.next();

      // Be sure we are reading a section code
      if (curr.code !== 2) {
        console.error(
          `Unexpected code ${debugCode(curr)} after 0:${DXFSymbol.Section}`,
        );
        curr = scanner.next();
        continue;
      }

      //Handle section names (HEADER, BLOCKS, ENTITIES, TABLES)
      log.debug(`> ${curr.value}`);
      switch (curr.value) {
        case DXFSymbol.Header:
          dxf.header = parseHeader(scanner);
          break;
        case DXFSymbol.Blocks:
          dxf.blocks = parseBlocks(parseState);
          break;
        case DXFSymbol.Entities:
          dxf.entities = parseEntities(parseState, false);
          break;
        case DXFSymbol.Tables:
          dxf.tables = parseTables(scanner);
          break;
        case DXFSymbol.Classes:
          dxf.classes = parseClasses(scanner);
          break;
        case DXFSymbol.Objects:
          dxf.objects = parseObjects(scanner);
          break;
        case DXFSymbol.EOF:
          log.debug(DXFSymbol.EOF);
          break;
        default:
          //TODO: Consider caching the content of this skipped section for serialization
          log.warn(`Skipping section '${curr.value}'`);
      }
      log.debug('<');
      curr = scanner.lastReadGroup as IGroup<GroupValue>;
    } else {
      curr = scanner.next();
    }
    // If is a new section
  }
};

const addNewlines = function* (
  lines: IterableIterator<string>,
): IterableIterator<string> {
  //Yield lines seperated by new lines. Does not include trailing new line
  const firstLine = lines.next();
  if (!firstLine.done) {
    yield firstLine.value; //first line
    for (const line of lines) {
      yield '\n'; //new line after preceeding line if !done
      yield line;
    }
  }
};

export default class DxfParser {
  private _entityHandlers: Partial<Record<EntityName, IGeometry<IEntity>>> = {};
  constructor() {
    registerDefaultEntityHandlers(this);
  }

  public parse(source: string): IDxf | null {
    if (typeof source === 'string') {
      return this._parse(source);
    } else {
      console.error('Cannot read dxf source of type `' + typeof source);
      return null;
    }
  }

  public registerEntityHandler<T extends IEntity>(
    handlerType: new () => IGeometry<T>,
  ): void {
    const instance = new handlerType();
    this._entityHandlers[instance.ForEntityName] = instance;
  }

  public parseSync(source: string): IDxf | null {
    return this.parse(source);
  }

  public parseStream(stream: Readable): Promise<IDxf> {
    let dxfString = '';
    //eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    return new Promise<IDxf>((res, rej) => {
      stream.on('data', (chunk) => {
        dxfString += chunk;
      });
      stream.on('end', () => {
        try {
          res(self._parse(dxfString));
        } catch (err) {
          rej(err);
        }
      });
      stream.on('error', (err) => {
        rej(err);
      });
    });
  }

  //TODO
  //public parseBinary(source: Blob | ArrayBuffer): IDxf {
  //}

  //TODO
  //public parseBinaryStream(stream: Readable): Promise<IDxf> {
  //}

  public *serialize(dxf: IDxf): IterableIterator<string> {
    for (const section of Object.keys(dxf) as (keyof IDxf)[]) {
      //Start of section
      yield '0';
      yield DXFSymbol.Section;
      switch (section) {
        case 'header':
          yield* serializeHeader(dxf.header as Header);
          break;
        case 'entities':
          yield* serializeEntities(
            dxf.entities as DxfEntity[],
            this._entityHandlers,
          );
          break;
        case 'blocks':
          yield* serializeBlocks(dxf.blocks as Blocks, this._entityHandlers);
          break;
        case 'classes':
          yield* serializeClasses(dxf.classes as IClasses);
          break;
        case 'tables':
          yield* serializeTables(dxf.tables as ITables);
          break;
        case 'objects':
          yield* serializeObjects(dxf.objects as IObject[]);
          break;
      }
      //End of section
      yield '0';
      yield DXFSymbol.EndSec;
    }
    yield '0';
    yield DXFSymbol.EOF;
  }

  public serializeToString(dxf: IDxf): string {
    let acc = '';
    for (const line of addNewlines(this.serialize(dxf))) {
      acc += line;
    }
    return acc;
  }

  public serializeToBlob(dxf: IDxf): Blob {
    //For NodeJS < 18, Blob is defined on buffer.Blob. For NodeJS, >=18, Blob is a global
    //Blob constructor support Iterator as argument, but typescript is wrong, so case `as unknown as string[]`
    return new Blob(addNewlines(this.serialize(dxf)) as unknown as string[]);
  }

  //public serializeBinaryStream(dxf: IDxf): stream {
  //}

  private _parse(dxfString: string): IDxf {
    const dxfLinesArray = dxfString.split(/\r\n|\r|\n/g);
    const scanner = new DxfArrayScanner(dxfLinesArray);
    if (!scanner.hasNext()) throw Error('Empty file');
    const dxf = {} as IDxf;
    parseAll({
      scanner,
      dxf,
      entityHandlers: this._entityHandlers,
      lastHandle: 0,
    });
    return dxf;
  }
}

// const BLOCK_ANONYMOUS_FLAG = 1;
// const BLOCK_NON_CONSTANT_FLAG = 2;
// const BLOCK_XREF_FLAG = 4;
// const BLOCK_XREF_OVERLAY_FLAG = 8;
// const BLOCK_EXTERNALLY_DEPENDENT_FLAG = 16;
// const BLOCK_RESOLVED_OR_DEPENDENT_FLAG = 32;
// const BLOCK_REFERENCED_XREF = 64;

/* Notes */
// Code 6 of an entity indicates inheritance of properties (eg. color).
//   BYBLOCK means inherits from block
//   BYLAYER (default) mean inherits from layer
